<!--
  QuickStartTour.vue - Reusable first-visit tutorial dialog.

  Drop into any aside page that has its own tour. The component is fully
  self-contained:
    - Loads the per-user quick-start flags via the Pinia store on mount.
    - Shows the dialog iff the user has not dismissed this tour.
    - Exit semantics:
        * Skip for now -> closes locally only. Tour reshows on the next
          page load. No POST, no DB bit set.
        * Got it       -> closes AND POSTs to set the bit. Tour is gone
          for this user across browsers and devices.
        * Deep-link CTA buttons -> same as Got it (clicking a CTA means
          the user engaged with the content, so they have seen it).

  Pages provide their content via the `steps` prop - an array of `{ title,
  body, cta?: { label, icon, path } }`.

  Tour keys (and bit indices) are defined in src/stores/quickStart.js -
  unknown keys are silently ignored.
-->
<template>
    <q-dialog v-model="show" persistent>
        <q-card style="min-width: 420px; max-width: 560px;">
            <q-card-section class="row items-center q-pb-sm">
                <q-icon name="rocket_launch" color="primary" size="32px" class="q-mr-md" />
                <div class="col">
                    <div class="text-h6">{{ title }}</div>
                    <div class="text-caption text-grey-7">{{ subtitle }}</div>
                </div>
            </q-card-section>
            <q-separator />

            <q-card-section>
                <ol class="quickstart-list q-pl-md q-mb-none">
                    <li v-for="(step, idx) in steps" :key="idx" class="q-mb-md">
                        <div class="text-subtitle2">{{ step.title }}</div>
                        <div class="text-body2 text-grey-8" :class="step.cta ? 'q-mb-xs' : ''">
                            {{ step.body }}
                        </div>
                        <q-btn
                            v-if="step.cta"
                            outline dense no-caps size="sm"
                            color="primary"
                            :icon="step.cta.icon || undefined"
                            :label="step.cta.label"
                            @click="dismissAndGo(step.cta.path)"
                        />
                    </li>
                </ol>
            </q-card-section>

            <q-separator />

            <q-card-actions align="right">
                <q-btn flat no-caps label="Skip for now" @click="skipForNow" />
                <q-btn unelevated no-caps color="primary" label="Got it" @click="dismiss" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import { mapState } from 'pinia';
import { useQuickStartStore, TOUR_BITS } from '@stores/quickStart.js';

export default {
    name: 'QuickStartTour',

    props: {
        // Key into TOUR_BITS - e.g. 'dashboard', 'packages'. Pages that pass
        // an unknown key are silently ignored (the dialog never shows) so a
        // typo cannot pop a stuck dialog on every visit.
        tourKey: { type: String, required: true },
        title:    { type: String, required: true },
        subtitle: { type: String, default: 'A 30-second tour - feel free to skip.' },
        // Each step: { title, body, cta?: { label, icon?, path } }.
        steps:    { type: Array,  required: true }
    },

    data() {
        return { show: false };
    },

    computed: {
        ...mapState(useQuickStartStore, ['loaded']),
        knownTour() { return Object.prototype.hasOwnProperty.call(TOUR_BITS, this.tourKey); }
    },

    watch: {
        // Once flags load, decide once whether to show. Subsequent flag changes
        // (other tabs dismissing tours) don't re-trigger - the dialog is a
        // one-shot per page visit.
        loaded(now) {
            if (now) this.maybeShow();
        }
    },

    async mounted() {
        if (!this.knownTour) {
            console.warn('[QuickStartTour] unknown tour key:', this.tourKey);
            return;
        }
        const store = useQuickStartStore();
        await store.load();
        this.maybeShow();
    },

    methods: {
        maybeShow() {
            if (!this.knownTour) return;
            const store = useQuickStartStore();
            if (!store.isDismissed(this.tourKey)) {
                this.show = true;
            }
        },

        // Close the dialog locally without writing the dismissal bit. The
        // tour shows again on the next page load - intended for the "I'm
        // busy, remind me later" path.
        skipForNow() {
            this.show = false;
        },

        // Close AND persist the dismissal so this tour never shows again
        // for this user. Used by Got it and by deep-link CTAs (clicking a
        // CTA = the user engaged with the content, so they have seen it).
        dismiss() {
            this.show = false;
            const store = useQuickStartStore();
            store.dismiss(this.tourKey);
        },

        dismissAndGo(path) {
            this.dismiss();
            window.location.href = path;
        }
    }
};
</script>

<style scoped>
.quickstart-list {
    font-size: 1rem;
}
.quickstart-list li::marker {
    font-weight: 600;
    color: var(--q-primary, #6366F1);
}
.quickstart-list li {
    padding-left: 4px;
}
</style>
